// SAM-683 — display-only number/date formatting by language. Never used to build a request
// or to compare against backend data: those keep the raw values.
import type { Lang } from './lang';

/** Every business is in the Dominican Republic; dates and times show in its clock. */
export const TIME_ZONE = 'America/Santo_Domingo';

export const LOCALE: Record<Lang, string> = { en: 'en-US', es: 'es-DO' };

/** Same symbols the service editor and /pay already show (SAM-198): RD$ for DOP, US$ for USD. */
const CURRENCY_SYMBOL: Readonly<Record<string, string>> = { DOP: 'RD$', USD: 'US$' };

export const currencySymbol = (currency: string | null | undefined): string => {
  const code = String(currency ?? '').trim().toUpperCase();
  if (!code) return '$';
  return CURRENCY_SYMBOL[code] || code;
};

/**
 * "RD$ 1,250.00" / "US$ 85.50". Always two decimals: the admin shows ledgers, where
 * "RD$ 1,250" and "RD$ 1,250.00" next to each other read as different amounts.
 * '' for a missing or non-numeric amount.
 */
export const formatMoney = (
  amount: number | string | null | undefined,
  currency: string | null | undefined,
  lang: Lang,
): string => {
  if (amount === null || amount === undefined || amount === '') return '';
  const n = typeof amount === 'number' ? amount : Number(String(amount).replace(/,/g, ''));
  if (!Number.isFinite(n)) return '';
  const digits = new Intl.NumberFormat(LOCALE[lang], { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(n));
  return `${n < 0 ? '-' : ''}${currencySymbol(currency)} ${digits}`;
};

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A 'YYYY-MM-DD' is a calendar day, not an instant: format it as that day whatever the
 * viewer's clock. Anything else is an instant shown in Santo Domingo time.
 */
const toDateAndZone = (value: string | number | Date): { date: Date; timeZone: string } | null => {
  if (typeof value === 'string' && DATE_ONLY.test(value)) {
    const date = new Date(`${value}T12:00:00Z`);
    return Number.isNaN(date.getTime()) ? null : { date, timeZone: 'UTC' };
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : { date, timeZone: TIME_ZONE };
};

const fmt = (value: string | number | Date | null | undefined, lang: Lang, opts: Intl.DateTimeFormatOptions): string => {
  if (value === null || value === undefined || value === '') return '';
  const dz = toDateAndZone(value);
  if (!dz) return String(value);
  return new Intl.DateTimeFormat(LOCALE[lang], { ...opts, timeZone: dz.timeZone }).format(dz.date);
};

/** "Oct 1, 2026" / "1 oct 2026". */
export const formatDate = (value: string | number | Date | null | undefined, lang: Lang): string =>
  fmt(value, lang, { day: 'numeric', month: 'short', year: 'numeric' });

/** "Oct 1, 2026, 3:05 PM" / "1 oct 2026, 3:05 p. m.". */
export const formatDateTime = (value: string | number | Date | null | undefined, lang: Lang): string =>
  fmt(value, lang, { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });

/** "3:05 PM" / "3:05 p. m.". */
export const formatTime = (value: string | number | Date | null | undefined, lang: Lang): string =>
  fmt(value, lang, { hour: 'numeric', minute: '2-digit' });
